import { EAssetType } from 'constants/enums';
import * as z from 'zod';

import { Tab } from '../../Tabs/Tabs';

const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/svg',
  'image/webp',
  'application/json'
];

export enum EImageUploadModalTabs {
  UPLOAD = 'upload',
  MEDIA = 'media'
}

export const IMAGE_UPLOAD_TABS: Tab[] = [
  { id: EImageUploadModalTabs.UPLOAD, value: 'Upload' },
  { id: EImageUploadModalTabs.MEDIA, value: 'Media' }
];

export const MEDIA_TYPE_OPTIONS = [
  {
    value: EAssetType.GENERAL,
    label: 'General'
  },
  {
    value: EAssetType.RECEIPT_LOGO,
    label: 'Receipt Logo'
  },
  {
    value: EAssetType.PRODUCT,
    label: 'Products'
  },
  {
    value: EAssetType.LOGO,
    label: 'Logo'
  },
  {
    value: EAssetType.BG_MOBILE,
    label: 'Mobile background'
  },
  {
    value: EAssetType.BG_DESKTOP,
    label: 'Desktop background'
  },
  {
    value: EAssetType.BG_BUNDLE,
    label: 'Offer background'
  },
  {
    value: EAssetType.BG_POPUP,
    label: 'PopUp background'
  },
  {
    value: EAssetType.FAVICON,
    label: 'Favicon'
  },
  {
    value: EAssetType.PRODUCT_PREFIX,
    label: 'Product Prefix'
  },
  {
    value: EAssetType.BANNER,
    label: 'Banner'
  },
  {
    value: EAssetType.PLAYER_PROFILE_IMG,
    label: 'Player profile'
  },
  {
    value: EAssetType.SECTION,
    label: 'Section'
  },
  {
    value: EAssetType.ADD_TO_HOME_BUTTON_IMAGE,
    label: 'Add to home button image'
  },
  {
    value: EAssetType.ADD_TO_HOME_ICON_IMAGE,
    label: 'Add to home icon image'
  },
  {
    value: EAssetType.BACK_TO_GAME_BUTTON_IMAGE,
    label: 'Back To Game Button'
  },
  {
    value: EAssetType.TRAIT,
    label: 'Trait'
  },
  {
    value: EAssetType.BUTTON_ASSET_SUFFIX,
    label: 'Button Asset Suffix'
  },
  {
    value: EAssetType.RECEIPT_HEADER,
    label: 'Receipt Header'
  },
  {
    value: EAssetType.ANIMATION,
    label: 'Animation'
  },
  {
    value: EAssetType.BADGE,
    label: 'Badge'
  },
  {
    value: EAssetType.OFFER_HEADER,
    label: 'Offer Header'
  },
  {
    value: EAssetType.SEO,
    label: 'SEO'
  },
  {
    value: EAssetType.CUSTOM_ARROW,
    label: 'Gallery arrows (Right Arrow Only)'
  }
];

export const DEFAULT_BUILTIN_ANIMATION_LOADER_MEDIA_ID = 'default-loader';

export const DEFAULT_BUILTIN_ANIMATION_LOADER_URL =
  'https://media-dev.appcharge.com/media/defaults/circular-loader.json';

export const DEFAULT_BUILTIN_ANIMATION_LOADER = {
  name: 'Default Loader',
  url: DEFAULT_BUILTIN_ANIMATION_LOADER_URL,
  type: EAssetType.ANIMATION,
  mediaId: DEFAULT_BUILTIN_ANIMATION_LOADER_MEDIA_ID,
  mimeType: 'application/json'
};

/** Same URL as `defaultGeneralTheme.favicon` */
export const DEFAULT_BUILTIN_FAVICON_MEDIA_ID = 'default-favicon';

export const DEFAULT_BUILTIN_FAVICON_URL =
  'https://media.appcharge.com/defaults/default-store-logo.svg';

export const DEFAULT_BUILTIN_FAVICON = {
  name: 'Default Favicon',
  url: DEFAULT_BUILTIN_FAVICON_URL,
  type: EAssetType.FAVICON,
  mediaId: DEFAULT_BUILTIN_FAVICON_MEDIA_ID,
  mimeType: 'image/svg+xml'
};

export const DEFAULT_BUILTIN_PLAYER_PROFILE_MEDIA_ID = 'default-player-profile';

export const DEFAULT_BUILTIN_PLAYER_PROFILE_URL =
  'https://media.appcharge.com/defaults/old-profile.png';

export const DEFAULT_BUILTIN_PLAYER_PROFILE = {
  name: 'Default Player Profile',
  url: DEFAULT_BUILTIN_PLAYER_PROFILE_URL,
  type: EAssetType.PLAYER_PROFILE_IMG,
  mediaId: DEFAULT_BUILTIN_PLAYER_PROFILE_MEDIA_ID,
  mimeType: 'image/png'
};

export const BUILTIN_MEDIA_IDS = new Set<string>([
  DEFAULT_BUILTIN_ANIMATION_LOADER_MEDIA_ID,
  DEFAULT_BUILTIN_FAVICON_MEDIA_ID,
  DEFAULT_BUILTIN_PLAYER_PROFILE_MEDIA_ID
]);

export const IMG_UPLOAD_FORM_SCHEMA = z
  .discriminatedUnion('isExtrenalImageUrl', [
    z.object({
      mediaId: z.string(),
      isExtrenalImageUrl: z.literal(true),
      externalImageUrl: z.string().url(),
      type: z.nativeEnum(EAssetType),
      image: z.union([z.instanceof(File), z.null()]).optional(),
      tab: z.enum(['upload', 'media'])
    }),
    z.object({
      mediaId: z.string(),
      isExtrenalImageUrl: z.literal(false).optional(),
      externalImageUrl: z.string().url().optional(),
      type: z.nativeEnum(EAssetType),
      image: z.union([z.instanceof(File), z.null()]),
      tab: z.enum(['upload', 'media'])
    })
  ])
  .refine(
    (data) => {
      if (data.tab === 'upload') {
        if (data.isExtrenalImageUrl) {
          return data.mediaId?.trim();
        } else {
          return data.mediaId?.trim() && data.image;
        }
      }
      return true;
    },
    {
      message: 'Please fill required fields for upload tab',
      path: ['mediaId']
    }
  );

export interface ExternalImageData {
  externalImageUrl: string;
  name: string;
  type: EAssetType;
}
